/**
 * codedFieldControls.tsx — kontrolki dla pól o ZAMKNIĘTYM, USTAWOWYM zbiorze wartości
 * (znaczniki ewidencji KSeF/JPK, tryb wystawienia, kanał wpływu dokumentu).
 *
 * Wyciągnięte z `purchaseInvoiceCoreRenderers.tsx`, gdy te same pola pojawiły się na
 * fakturach sprzedaży — pola ewidencji KSeF dotyczą OBU klas faktur, a druga kopia selecta
 * rozjechałaby się przy pierwszej zmianie (np. obsłudze wartości spoza zbioru).
 *
 * NIE dla pól słownikowych rozszerzalnych przez tenanta — tam jest `DictionarySelect`
 * (kontrolka z hokiem `useDictionary`). Różnica jest zamierzona: te zbiory są ustawowe i
 * nikt nie ma prawa ich poszerzyć, dlatego kody biorą się z `@saas/shared`, nie z bazy.
 */

import { useTranslation } from 'react-i18next';

import { Dropdown, type DropdownOption } from '@/components/Dropdown';

/**
 * Select po ZAMKNIĘTYM zbiorze kodów; etykiety tłumaczy przekazany `labelFor`.
 *
 * Wartość zapisana, ale spoza zbioru (rozjazd po imporcie/legacy) NIE ZNIKA — dokładamy ją
 * jako dodatkową opcję, żeby edycja rekordu nie skasowała jej po cichu.
 */
export function CodedSelect({
  value,
  onChange,
  codes,
  labelFor,
  disabled,
  testId,
  emptyLabel = '— wybierz —',
}: {
  value: string;
  onChange: (v: string) => void;
  codes: readonly string[];
  labelFor: (code: string, t: (key: string) => string) => string;
  disabled?: boolean;
  testId?: string;
  emptyLabel?: string;
}) {
  // Hook w pod-komponencie, bo czyste lambdy rendererów nie mogą go wywołać.
  const { t } = useTranslation();
  const options: DropdownOption[] = codes.map((code) => ({
    value: code,
    label: labelFor(code, t),
  }));
  const isKnown = !value || codes.includes(value);
  return (
    <Dropdown
      options={[
        { value: '', label: emptyLabel },
        ...(isKnown ? [] : [{ value, label: value }]),
        ...options,
      ]}
      value={value}
      onChange={onChange}
      disabled={disabled}
      data-testid={testId}
    />
  );
}

/**
 * Znacznik czasu z KSeF w formacie czytelnym dla księgowej. Repo nie ma wspólnego formatera
 * daty-czasu — każdy ekran ma własny; ten obsługuje pola ewidencji na obu klasach faktur.
 * Wartość nieparsowalna wraca surowa (lepiej pokazać śmieć niż „Invalid Date").
 */
export function formatTimestampPl(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString('pl-PL');
}
